import React, { useState } from "react";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import {
  Clock,
  User,
  Check,
  X,
  RefreshCw,
  MessageCircle,
  Coins,
  Target
} from "lucide-react";

// Format mesos: input is in millions, display as "X,000,000 mesos"
const formatMesos = (value) => {
  if (!value && value !== 0) return "";
  const num = parseFloat(value);
  if (isNaN(num)) return value; // fallback for legacy text values
  const full = num * 1000000;
  return `${full.toLocaleString()} mesos`;
};

// Status badge colors
const getStatusColor = (status) => {
  switch (status) {
    case "ACTIVE": return "bg-emerald-600 text-white";
    case "ASSIGNED": return "bg-sky-600 text-white";
    case "SUCCEEDED": return "bg-amber-500 text-black";
    case "INACTIVE": return "bg-stone-500 text-white";
    case "CANCELLED": return "bg-red-600 text-white";
    default: return "bg-stone-500 text-white";
  }
};

const BountyPoster = ({
  bounty,
  isOwner = false,
  isHunter = false,
  isAcceptedHunter = false,
  isAdmin = false,
  onAccept,
  onCancel,
  onRestore,
  onConfirm,
  onReport,
  onViewDetails,
  onViewClient,
  onViewUser,
  onAdminCancel,
  bossImage,
  compact = false
}) => {
  const [showDetailModal, setShowDetailModal] = useState(false);
  
  const formatTimeRemaining = (expiresAt) => {
    const now = new Date();
    const expires = new Date(expiresAt);
    const diff = expires - now;
    
    if (diff <= 0) return "Expired";
    
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
  };

  // Compact version for boss detail header
  if (compact) {
    return (
      <>
        <div
          className="relative rounded-md overflow-hidden shadow-lg cursor-pointer"
          style={{
            background: bounty.status === "ASSIGNED" 
              ? "linear-gradient(180deg, #1e3a5f 0%, #0c1f3a 50%, #081428 100%)"
              : "linear-gradient(180deg, #7f1d1d 0%, #450a0a 50%, #1c0505 100%)",
            border: bounty.status === "ASSIGNED" ? "3px solid #3b82f6" : "3px solid #dc2626"
          }}
          onClick={() => setShowDetailModal(true)}
        >
          <div className="p-3 flex items-center gap-3">
            {/* Boss Image */}
            <div 
              className="w-14 h-14 rounded-full flex-shrink-0 flex items-center justify-center"
              style={{
                background: "linear-gradient(180deg, #991b1b 0%, #450a0a 100%)",
                border: "2px solid #f87171",
                boxShadow: "0 0 10px rgba(248,113,113,0.3)"
              }}
            >
              <img
                src={bossImage}
                alt={bounty.boss_name}
                className="w-10 h-10 object-contain"
                onError={(e) => {
                  e.target.src = "https://legends.ml/static/images/lib/monster/6130101.png";
                }}
              />
            </div>
            
            {/* Info */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span 
                  className="text-base font-black tracking-wider"
                  style={{ 
                    color: "#fbbf24",
                    textShadow: "1px 1px 2px #000, 0 0 10px rgba(251,191,36,0.5)"
                  }}
                >
                  BOUNTY
                </span>
                <Badge className={`${getStatusColor(bounty.status)} text-[10px] px-1.5 py-0`}>
                  {bounty.status}
                </Badge>
              </div>
              <p className="font-bold text-sm truncate text-amber-300">
                {formatMesos(bounty.reward)}
                {bounty.reward_apr_amount > 0 && <span className="text-purple-300 text-xs ml-1">+APR x{bounty.reward_apr_amount}</span>}
                {bounty.reward_onyx_apple_amount > 0 && <span className="text-purple-300 text-xs ml-1">+Onyx x{bounty.reward_onyx_apple_amount}</span>}
              </p>
              <p className="text-xs text-red-300/70">
                {bounty.contact_igns.slice(0, 2).join(", ")} • {formatTimeRemaining(bounty.expires_at)}
              </p>
            </div>
            
            {/* Accept Button */}
            {isHunter && bounty.status === "ACTIVE" && (
              <Button
                size="sm"
                className="font-bold text-xs px-3"
                style={{
                  background: "linear-gradient(180deg, #16a34a 0%, #15803d 100%)",
                  border: "2px solid #22c55e",
                  textShadow: "1px 1px 1px rgba(0,0,0,0.5)"
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  onAccept?.();
                }}
              >
                <Check className="w-3 h-3 mr-1" />
                Accept
              </Button>
            )}
          </div>
        </div>
        
        {/* Detail Modal */}
        <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
          <DialogContent 
            className="max-w-md p-0 overflow-hidden"
            style={{
              background: "linear-gradient(180deg, #7f1d1d 0%, #450a0a 100%)",
              border: "4px solid #dc2626"
            }}
          >
            <div className="p-6">
              {/* BOUNTY Header */}
              <div className="text-center mb-4">
                <div className="flex items-center justify-center gap-2 mb-2">
                  <Target className="w-6 h-6 text-amber-400" />
                  <h2 
                    className="text-3xl font-black tracking-widest"
                    style={{ 
                      color: "#fbbf24",
                      textShadow: "2px 2px 4px #000, 0 0 20px rgba(251,191,36,0.5)"
                    }}
                  >
                    BOUNTY
                  </h2>
                  <Target className="w-6 h-6 text-amber-400" />
                </div>
                <Badge className={`${getStatusColor(bounty.status)} px-3 py-1`}>
                  {bounty.status}
                </Badge>
              </div>
              
              {/* Boss Image */}
              <div className="flex justify-center mb-4">
                <div 
                  className="w-28 h-28 rounded-lg flex items-center justify-center"
                  style={{
                    background: "linear-gradient(180deg, #991b1b 0%, #450a0a 100%)",
                    border: "3px solid #f87171",
                    boxShadow: "0 0 20px rgba(248,113,113,0.3)"
                  }}
                >
                  <img
                    src={bossImage}
                    alt={bounty.boss_name}
                    className="w-20 h-20 object-contain"
                  />
                </div>
              </div>
              
              {/* Boss Name */}
              <div className="text-center mb-4">
                <h3 className="text-xl font-bold text-red-100">
                  {bounty.boss_name.split(" - ")[0]}
                </h3>
                {bounty.boss_name.includes(" - ") && (
                  <p className="text-sm text-red-300/70">{bounty.boss_name.split(" - ")[1]}</p>
                )}
              </div>
              
              {/* Reward */}
              <div 
                className="text-center mb-4 p-3 rounded-lg"
                style={{ background: "rgba(251,191,36,0.15)", border: "1px solid rgba(251,191,36,0.3)" }}
              >
                <p className="text-xs text-amber-300/70 uppercase tracking-wider mb-1">Reward</p>
                <p className="text-2xl font-black text-amber-400">
                  {formatMesos(bounty.reward)}
                </p>
                {(bounty.reward_apr_amount || bounty.reward_onyx_apple_amount) && (
                  <div className="flex items-center justify-center gap-2 mt-2">
                    {bounty.reward_apr_amount > 0 && (
                      <div className="flex items-center gap-1 px-2 py-1 bg-purple-500/20 rounded border border-purple-500/30">
                        <img src="https://legends.ml/static/images/lib/item/05050000.png" alt="APR" className="w-5 h-5" />
                        <span className="text-sm text-purple-300 font-bold">APR x{bounty.reward_apr_amount}</span>
                      </div>
                    )}
                    {bounty.reward_onyx_apple_amount > 0 && (
                      <div className="flex items-center gap-1 px-2 py-1 bg-purple-500/20 rounded border border-purple-500/30">
                        <img src="https://royals-library.netlify.app/images/items/02022179.png" alt="Onyx Apple" className="w-5 h-5" />
                        <span className="text-sm text-purple-300 font-bold">Onyx x{bounty.reward_onyx_apple_amount}</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
              
              {/* Custom Message */}
              {bounty.message && (
                <div className="mb-4 p-3 rounded-lg bg-black/30">
                  <p className="text-xs text-red-300/70 uppercase tracking-wider mb-1">
                    <MessageCircle className="w-3 h-3 inline mr-1" />
                    Message
                  </p>
                  <p className="text-sm text-red-100">{bounty.message}</p>
                </div>
              )}
              
              {/* Contact Info */}
              <div className="mb-4">
                <p className="text-xs text-amber-300/70 text-center uppercase tracking-wider mb-2">
                  Contact In-Game
                </p>
                <div className="flex flex-wrap justify-center gap-2">
                  {bounty.contact_igns.map((ign, i) => (
                    <span 
                      key={i} 
                      className="px-3 py-1 rounded font-bold text-sm"
                      style={{
                        background: "linear-gradient(180deg, #fbbf24 0%, #f59e0b 100%)",
                        color: "#78350f"
                      }}
                    >
                      {ign}
                    </span>
                  ))}
                </div>
              </div>
              
              {/* Client & Time */}
              <div className="flex justify-between items-center text-sm text-red-200 mb-4">
                <div className="flex items-center gap-2 cursor-pointer hover:text-amber-300" onClick={(e) => { e.stopPropagation(); onViewClient?.(); }}>
                  <User className="w-4 h-4" />
                  <span>{bounty.client_username}</span>
                </div>
                {bounty.status === "ACTIVE" && (
                  <div className="flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    <span>{formatTimeRemaining(bounty.expires_at)}</span>
                  </div>
                )}
              </div>
              
              {/* Action Button */}
              {isHunter && bounty.status === "ACTIVE" && (
                <Button
                  className="w-full font-black text-lg py-3"
                  style={{
                    background: "linear-gradient(180deg, #16a34a 0%, #15803d 100%)",
                    border: "3px solid #22c55e"
                  }}
                  onClick={() => {
                    onAccept?.();
                    setShowDetailModal(false);
                  }}
                >
                  <Check className="w-5 h-5 mr-2" />
                  ACCEPT BOUNTY
                </Button>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </>
    );
  }
  
  // Full poster version - matching expedition style with red/gold theme
  return (
    <div
      className="relative rounded-lg overflow-hidden shadow-2xl transform hover:scale-[1.02] transition-all cursor-pointer"
      style={{
        background: bounty.status === "ASSIGNED"
          ? "linear-gradient(180deg, #1e3a5f 0%, #0c1f3a 50%, #081428 100%)"
          : "linear-gradient(180deg, #7f1d1d 0%, #450a0a 50%, #1c0505 100%)",
        border: bounty.status === "ASSIGNED" ? "3px solid #3b82f6" : "3px solid #dc2626",
        boxShadow: bounty.status === "ASSIGNED" 
          ? "0 8px 32px rgba(59,130,246,0.3), inset 0 1px 0 rgba(255,255,255,0.1)"
          : "0 8px 32px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.1)"
      }}
      onClick={onViewDetails}
    >
      {/* Glowing top border */}
      <div 
        className="absolute top-0 left-0 right-0 h-1"
        style={{ background: "linear-gradient(90deg, transparent, #fbbf24, transparent)" }}
      />
      
      {/* Content */}
      <div className="relative p-5 min-h-[380px] flex flex-col">
        {/* Status Badge */}
        <div className="absolute top-3 right-3 z-10">
          <Badge className={`${getStatusColor(bounty.status)} font-bold text-xs shadow-lg px-2 py-1`}>
            {bounty.status}
          </Badge>
        </div>
        
        {/* BOUNTY Title */}
        <div className="text-center mb-3">
          <div className="flex items-center justify-center gap-2">
            <Target className="w-5 h-5 text-amber-400" />
            <h3 
              className="text-2xl font-black tracking-widest"
              style={{ 
                color: "#fbbf24",
                textShadow: "0 0 20px rgba(251,191,36,0.5), 0 2px 4px rgba(0,0,0,0.5)"
              }}
            >
              BOUNTY
            </h3>
            <Target className="w-5 h-5 text-amber-400" />
          </div>
          <div className="text-[10px] text-amber-300/70 tracking-[0.3em]">WANTED: DEAD</div>
        </div>
        
        {/* Boss Image with glow */}
        <div className="flex justify-center mb-3">
          <div 
            className="w-24 h-24 rounded-lg flex items-center justify-center"
            style={{
              background: "linear-gradient(180deg, #991b1b 0%, #450a0a 100%)",
              border: "3px solid #f87171",
              boxShadow: "0 0 20px rgba(248,113,113,0.3), inset 0 2px 4px rgba(0,0,0,0.3)"
            }}
          >
            <img
              src={bossImage}
              alt={bounty.boss_name}
              className="w-16 h-16 object-contain"
              onError={(e) => {
                e.target.src = "https://legends.ml/static/images/lib/monster/6130101.png";
              }}
            />
          </div>
        </div>
        
        {/* Boss Name */}
        <div className="text-center mb-3">
          <h4 className="text-xl font-bold text-red-100">
            {bounty.boss_name.split(" - ")[0]}
          </h4>
          {bounty.boss_name.includes(" - ") && (
            <p className="text-xs text-red-300/70">{bounty.boss_name.split(" - ")[1]}</p>
          )}
        </div>
        
        {/* Reward */}
        <div 
          className="text-center mb-3 p-2 rounded-lg mx-2"
          style={{ background: "rgba(251,191,36,0.15)", border: "1px solid rgba(251,191,36,0.3)" }}
        >
          <p className="text-[10px] text-amber-300/70 uppercase tracking-wider">Reward</p>
          <p className="text-xl font-bold text-amber-400">{formatMesos(bounty.reward)}</p>
          {(bounty.reward_apr_amount || bounty.reward_onyx_apple_amount) && (
            <div className="flex items-center justify-center gap-2 mt-1">
              {bounty.reward_apr_amount > 0 && (
                <div className="flex items-center gap-1 px-1.5 py-0.5 bg-purple-500/20 rounded border border-purple-500/30">
                  <img src="https://legends.ml/static/images/lib/item/05050000.png" alt="APR" className="w-4 h-4" />
                  <span className="text-xs text-purple-300 font-bold">APR x{bounty.reward_apr_amount}</span>
                </div>
              )}
              {bounty.reward_onyx_apple_amount > 0 && (
                <div className="flex items-center gap-1 px-1.5 py-0.5 bg-purple-500/20 rounded border border-purple-500/30">
                  <img src="https://royals-library.netlify.app/images/items/02022179.png" alt="Onyx Apple" className="w-4 h-4" />
                  <span className="text-xs text-purple-300 font-bold">Onyx x{bounty.reward_onyx_apple_amount}</span>
                </div>
              )}
            </div>
          )}
        </div>
        
        {/* Custom Message */}
        {bounty.message && (
          <div className="mb-3 px-2">
            <p className="text-xs text-red-200/80 text-center italic">"{bounty.message}"</p>
          </div>
        )}
        
        {/* Contact IGNs */}
        <div className="text-center mb-3">
          <p className="text-[10px] text-amber-300/70 uppercase tracking-wider mb-2">Contact</p>
          <div className="flex flex-wrap justify-center gap-1">
            {bounty.contact_igns.map((ign, i) => (
              <span 
                key={i} 
                className="text-sm font-bold px-2 py-0.5 rounded"
                style={{
                  background: "linear-gradient(180deg, #fbbf24 0%, #f59e0b 100%)",
                  color: "#78350f"
                }}
              >
                {ign}
              </span>
            ))}
          </div>
        </div>
        
        {/* Time & Client */}
        <div className="flex items-center justify-between px-2 mb-3 text-sm">
          {bounty.status === "ACTIVE" && (
            <div className="flex items-center gap-1 text-red-200">
              <Clock className="w-4 h-4" />
              <span>{formatTimeRemaining(bounty.expires_at)}</span>
            </div>
          )}
          <div 
            className="flex items-center gap-1 text-red-200 cursor-pointer hover:text-amber-300"
            onClick={(e) => {
              e.stopPropagation();
              onViewClient?.();
            }}
          >
            {bounty.client_avatar ? (
              <img src={bounty.client_avatar} alt="" className="w-5 h-5 rounded-full border border-red-500" />
            ) : (
              <User className="w-4 h-4" />
            )}
            <span>{bounty.client_username}</span>
          </div>
        </div>
        
        {/* Action Buttons */}
        <div className="mt-auto space-y-2" onClick={(e) => e.stopPropagation()}>
          {/* Hunter viewing active bounty */}
          {isHunter && bounty.status === "ACTIVE" && (
            <Button
              className="w-full font-bold text-base py-2.5"
              style={{
                background: "linear-gradient(180deg, #16a34a 0%, #15803d 100%)",
                border: "2px solid #22c55e",
                boxShadow: "0 4px 15px rgba(34,197,94,0.3)"
              }}
              onClick={onAccept}
            >
              <Check className="w-5 h-5 mr-2" />
              ACCEPT BOUNTY
            </Button>
          )}
          
          {/* Owner actions */}
          {isOwner && bounty.status === "ACTIVE" && (
            <Button
              className="w-full font-bold bg-red-600 hover:bg-red-700"
              onClick={onCancel}
            >
              <X className="w-4 h-4 mr-2" />
              Cancel Bounty
            </Button>
          )}
          
          {isOwner && (bounty.status === "INACTIVE" || bounty.status === "CANCELLED") && (
            <Button
              className="w-full font-bold bg-amber-600 hover:bg-amber-700"
              onClick={onRestore}
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Restore Bounty
            </Button>
          )}
          
          {/* Confirmation for assigned bounties */}
          {(isOwner || isAcceptedHunter) && bounty.status === "ASSIGNED" && (
            <>
              {((isOwner && !bounty.client_confirmed) || (isAcceptedHunter && !bounty.hunter_confirmed)) && (
                <Button
                  className="w-full font-bold"
                  style={{
                    background: "linear-gradient(180deg, #16a34a 0%, #15803d 100%)",
                    border: "2px solid #22c55e"
                  }}
                  onClick={onConfirm}
                >
                  <Check className="w-4 h-4 mr-2" />
                  Confirm Completion
                </Button>
              )}
              
              {((isOwner && bounty.client_confirmed) || (isAcceptedHunter && bounty.hunter_confirmed)) && (
                <Badge className="w-full justify-center bg-green-600/30 text-green-300 py-2 border border-green-600">
                  <Check className="w-4 h-4 mr-2" />
                  Waiting for confirmation...
                </Badge>
              )}
            </>
          )}
          
          {/* Accepted hunter report buttons */}
          {isAcceptedHunter && bounty.status === "ASSIGNED" && (
            <>
              <Button
                variant="outline"
                className="w-full border-red-500 text-red-300 hover:bg-red-500/10"
                onClick={onCancel}
              >
                Release Bounty
              </Button>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-1 border-amber-500 text-amber-300 text-xs"
                  onClick={() => onReport?.("no_contact")}
                >
                  No Contact
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-1 border-red-500 text-red-300 text-xs"
                  onClick={() => onReport?.("no_payment")}
                >
                  No Payment
                </Button>
              </div>
            </>
          )}
          
          {/* Admin cancel button */}
          {isAdmin && bounty.status === "ACTIVE" && (
            <Button
              variant="outline"
              className="w-full border-red-500 text-red-300 hover:bg-red-500/20"
              onClick={onAdminCancel}
            >
              <X className="w-4 h-4 mr-2" />
              Admin Cancel
            </Button>
          )}
        </div>
        
        {/* Hunter indicator for assigned bounties */}
        {bounty.hunter_username && bounty.status === "ASSIGNED" && (
          <div className="absolute bottom-3 left-3" onClick={(e) => e.stopPropagation()}>
            <Badge
              className="text-xs bg-sky-500/30 border-sky-500 text-sky-300 cursor-pointer hover:bg-sky-500/40"
              onClick={() => onViewUser?.(bounty.hunter_id)}
              data-testid={`view-hunter-${bounty.id}`}
            >
              Hunter: {bounty.hunter_username}
            </Badge>
          </div>
        )}
        
        {/* Glowing bottom border */}
        <div 
          className="absolute bottom-0 left-0 right-0 h-1"
          style={{ background: "linear-gradient(90deg, transparent, #fbbf24, transparent)" }}
        />
      </div>
    </div>
  );
};

export default BountyPoster;
